import { faExclamationTriangle, faFolder, faLightbulb, faSave, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { fetchSupplierInfoForInvoice } from "../../../../store/slices/supplierInfoSlice";
import { getFormattedDate } from "../../../../utils/method";
import SUPPLIER_INFO_STATUS from "../../../../constants/supplier-info-status";
import "./StatusBanners.scss";

const StatusBanners = ({ supplierInfo, invoiceId, handleSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    if (!supplierInfo) return null;

    return (
        <>
            {/* Stored data banner */}
            {supplierInfo.isStored && (
                <Row className="justify-content-center mt-2">
                    <Col md={10} xl={10}>
                        <div className="d-flex align-items-center bg-light border-start border-info border-4 rounded p-2 shadow-sm">
                            <div className="flex-grow-1 d-flex align-items-center">
                                <div className="bg-info text-white rounded-circle p-2 me-2 d-flex justify-content-center align-items-center sb-icon-circle">
                                    <FontAwesomeIcon icon={faSave} className="fs-5" />
                                </div>
                                <div>
                                    <h6 className="mb-0 sb-banner-title">{t("supplier-info.saved-data", { defaultValue: "Saved Data" })}</h6>
                                    <small className="text-muted sb-banner-subtitle">
                                        {t("supplier-info.last-updated-by", { defaultValue: "Last updated by" })}
                                        {` ${supplierInfo?.updatedBy?.firstName || ''} ${supplierInfo?.updatedBy?.lastName || ''} `}
                                        {t("common.on", { defaultValue: "on" })}
                                        {' '}{getFormattedDate(supplierInfo?.updatedAt, true)}
                                    </small>
                                </div>
                            </div>

                            {/* Only show Update Data button if not in FINALIZED status */}
                            {supplierInfo.status !== SUPPLIER_INFO_STATUS.FINALIZED && (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => dispatch(fetchSupplierInfoForInvoice({ invoiceId, forceRefresh: true }))}
                                >
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    {t("supplier-info.update-data", { defaultValue: "Update Data" })}
                                </button>
                            )}
                        </div>
                    </Col>
                </Row>
            )}

            {/* Fresh data with stored version banner */}
            {!supplierInfo.isStored && supplierInfo.hasStoredVersion && (
                <Row className="justify-content-center mt-2">
                    <Col md={10} xl={10}>
                        <div className="d-flex align-items-center bg-light border-start border-warning border-4 rounded p-2 shadow-sm">
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-1">
                                    <div className="bg-warning text-dark rounded-circle p-2 me-2 d-flex justify-content-center align-items-center sb-icon-circle">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="fs-5" />
                                    </div>
                                    <div>
                                        <h6 className="mb-0 sb-banner-title">{t("supplier-info.fresh-data", { defaultValue: "Fresh Data" })}</h6>
                                        <small className="sb-banner-subtitle">
                                            {t("supplier-info.fresh-data-warning", { defaultValue: "You are viewing freshly calculated data that has not been saved yet." })}
                                        </small>
                                    </div>
                                </div>
                                <div className="ms-4 ps-2">
                                    <small className="d-block text-muted sb-stored-info">
                                        <strong>{t("supplier-info.stored-version-exists", { defaultValue: "A stored version exists" })}</strong>
                                        {" ("}
                                        {t("supplier-info.last-updated-by", { defaultValue: "last updated by" })}
                                        {` ${supplierInfo?.storedVersionInfo?.updatedBy?.firstName || ''} ${supplierInfo?.storedVersionInfo?.updatedBy?.lastName || ''} `}
                                        {t("common.on", { defaultValue: "on" })}
                                        {' '}{getFormattedDate(supplierInfo?.storedVersionInfo?.updatedAt, true)}
                                        {")"}
                                    </small>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {/* Always show View Stored Version button */}
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => dispatch(fetchSupplierInfoForInvoice({ invoiceId }))}
                                >
                                    <FontAwesomeIcon icon={faFolder} className="me-1" />
                                    {t("supplier-info.view-stored", { defaultValue: "View Stored Version" })}
                                </button>

                                {/* Only show Save This Data button if the stored version is not in FINALIZED status */}
                                {supplierInfo?.storedVersionInfo?.status !== "FINALIZED" && (
                                    <button
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => handleSubmit(null, false)}
                                    >
                                        <FontAwesomeIcon icon={faSave} className="me-1" />
                                        {t("supplier-info.save-fresh-data", { defaultValue: "Save This Data" })}
                                    </button>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Fresh data without stored version banner */}
            {!supplierInfo.isStored && !supplierInfo.hasStoredVersion && (
                <Row className="justify-content-center mt-2">
                    <Col md={10} xl={10}>
                        <div className="d-flex align-items-center bg-light border-start border-success border-4 rounded p-2 shadow-sm">
                            <div className="flex-grow-1 d-flex align-items-center">
                                <div className="bg-success text-white rounded-circle p-2 me-2 d-flex justify-content-center align-items-center sb-icon-circle">
                                    <FontAwesomeIcon icon={faLightbulb} className="fs-5" />
                                </div>
                                <div>
                                    <h6 className="mb-0 sb-banner-title">{t("supplier-info.new-data", { defaultValue: "New Data" })}</h6>
                                    <small className="sb-banner-subtitle">
                                        {t("supplier-info.fresh-data-new", { defaultValue: "This is freshly calculated data that has not been saved yet." })}
                                    </small>
                                </div>
                            </div>
                            {/* Always show Save Data button for new data */}
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleSubmit(null, false)}
                                >
                                    <FontAwesomeIcon icon={faSave} className="me-1" />
                                    {t("supplier-info.save-data", { defaultValue: "Save Data" })}
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => dispatch(fetchSupplierInfoForInvoice({ invoiceId, forceRefresh: true }))}
                                >
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    {t("supplier-info.update-data", { defaultValue: "Update Data" })}
                                </button>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default StatusBanners;
